import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Typography,
    Grid,
    Box,
    CircularProgress,
    Button,
    TextField,
    Badge,
    Avatar,
    Paper,
    Tooltip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    Star as StarIcon,
    LocationOn as LocationIcon,
    CheckCircle as CheckCircleIcon,
    SaveAlt as SaveIcon,
    Cancel as CancelIcon,
    Email,
    Warning as WarningIcon,
    Refresh as RefreshIcon,
    AddCircleOutline as AddCircleOutlineIcon,
    Search as SearchIcon,
    SwapHoriz as SwapHorizIcon,
    ChatBubbleOutline as ChatBubbleOutlineIcon,
    Payment as PaymentIcon,
    HelpOutline as HelpOutlineIcon,
    Info as InfoIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Circle, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '../context/AuthContext';
import { useMode } from '../context/ModeContext';
import {
    getCurrentUser,
    uploadProfilePicture,
    deleteProfilePicture,
    updateUser,
    logout,
    createConnectedAccount,
    createAccountLink,
    getAccountStatus,
    submitPersonaVerification,
    getStripeTransactions,
    deleteUserAccount,
} from '../services/api';
import Persona from 'persona';
import TransactionTable from '../components/TransactionTable';
import { LoadingButton } from '@mui/lab';

import FavoriteIcon from '@mui/icons-material/Favorite';
import { Collapse } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

// ----- Helper Function -----
function parsePythonListString(reqString) {
    if (!reqString || typeof reqString !== 'string') return [];
    const replaced = reqString.replace(/'/g, '"');
    try {
        return JSON.parse(replaced);
    } catch (err) {
        console.error('Failed to parse requirements:', reqString, err);
        return [];
    }
}

// ----- Styled Components -----
const StyledContainer = styled(Container)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    maxWidth: '600px',
    [theme.breakpoints.up('sm')]: {
        maxWidth: '600px',
    },
}));

const ProfileHeader = styled(Paper)(({ theme }) => ({
    position: 'relative',
    padding: theme.spacing(3),
    backgroundColor: '#ffffff',
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    marginBottom: theme.spacing(2),
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        backgroundColor: '#44b700',
        color: '#44b700',
        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
        '&::after': {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            animation: 'ripple 1.2s infinite ease-in-out',
            border: '1px solid currentColor',
            content: '""',
        },
    },
    '@keyframes ripple': {
        '0%': { transform: 'scale(.8)', opacity: 1 },
        '100%': { transform: 'scale(2.4)', opacity: 0 },
    },
}));

const LargeAvatar = styled(Avatar)(({ theme }) => ({
    width: theme.spacing(15),
    height: theme.spacing(15),
    border: `4px solid ${theme.palette.background.paper}`,
    boxShadow: theme.shadows[3],
}));

const StyledSection = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: '#ffffff',
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    marginBottom: theme.spacing(2),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': { borderRadius: theme.shape.borderRadius },
}));

const MapWrapper = styled(Box)(({ theme }) => ({
    height: '300px',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    marginBottom: theme.spacing(2),
}));

const ModeToggleContainer = styled(Paper)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-around',
    padding: theme.spacing(1),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
}));

const ToggleButton = styled(Button)(({ active, theme }) => ({
    flex: 1,
    margin: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: active ? '#34A853' : 'transparent',
    color: active ? '#FFFFFF' : '#767676',
    border: active ? 'none' : `1px solid #767676`,
    '&:hover': { backgroundColor: active ? '#2e8b46' : 'rgba(0,0,0,0.04)' },
}));

const QuickActionsContainer = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: '#FFFFFF',
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    marginBottom: theme.spacing(2),
}));

const QuickActionButton = styled(Button)(({ theme }) => ({
    width: '48%',
    marginBottom: theme.spacing(1),
    justifyContent: 'flex-start',
    textTransform: 'none',
}));

const SettingsItem = styled(ListItem)(({ theme }) => ({
    borderBottom: `1px solid #F0F0F0`,
}));

// ----- Sub-Components -----



// Quick actions buttons
const QuickActions = ({ mode, navigate }) => (
    <QuickActionsContainer>
        <Typography variant="h6" sx={{ mb: 1 }}>Quick Actions</Typography>
        <Grid container spacing={2}>
            <Grid item xs={6}>
                <QuickActionButton
                    variant="outlined"
                    startIcon={<FavoriteIcon style={{ color: '#34A853' }} />}
                    onClick={() => navigate('/wishlist')}
                    style={{ width: '100%' }}
                >
                    Favorites
                </QuickActionButton>
            </Grid>
            <Grid item xs={6}>
                <QuickActionButton
                    variant="outlined"
                    startIcon={mode === 'Lending' ? <AddCircleOutlineIcon /> : <SearchIcon />}
                    onClick={() => navigate(mode === 'Lending' ? '/create-listing' : '/')}
                    style={{ width: '100%' }}
                >
                    {mode === 'Lending' ? 'Add Listing' : 'Browse Items'}
                </QuickActionButton>
            </Grid>
            <Grid item xs={6}>
                <QuickActionButton
                    variant="outlined"
                    startIcon={<SwapHorizIcon />}
                    onClick={() => navigate('/transactions')}
                    style={{ width: '100%' }}
                >
                    Transactions
                </QuickActionButton>
            </Grid>
            <Grid item xs={6}>
                <QuickActionButton
                    variant="outlined"
                    startIcon={<ChatBubbleOutlineIcon />}
                    onClick={() => navigate('/conversations')}
                    style={{ width: '100%' }}
                >
                    Messages
                </QuickActionButton>
            </Grid>
        </Grid>
    </QuickActionsContainer>
);


// Profile card: displays profile picture, name, rating (with proper spacing), and location.
const ProfileCard = ({ user, isEditing, handleProfilePictureChange, handleDeleteProfilePicture }) => (
    <ProfileHeader>
        <Grid container spacing={2} alignItems="center">
            <Grid item>
                <input
                    accept="image/*"
                    id="profile-picture-input"
                    type="file"
                    hidden
                    onChange={handleProfilePictureChange}
                />
                <label htmlFor="profile-picture-input">
                    <StyledBadge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        variant="dot"
                    >
                        <LargeAvatar src={user.profile_picture} alt={user.first_name}>
                            {user.first_name?.[0]}
                        </LargeAvatar>
                    </StyledBadge>
                </label>
            </Grid>
            <Grid item xs>
                <Box display="flex" flexDirection="column" gap={1}>
                    <Typography variant="h5">
                        {`${user.first_name} ${user.last_name}`}
                    </Typography>
                    <Box display="flex" alignItems="center">
                        <StarIcon sx={{ color: '#FFD700', mr: 0.5 }} />
                        <Typography variant="body1">
                            {user.rating_average || '0.0'}{' '}
                            <Typography component="span" color="text.secondary">
                                ({user.reviews_count || 0} reviews)
                            </Typography>
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                        <LocationIcon color="action" />
                        <Typography variant="body1" sx={{ ml: 0.5 }}>
                            {user.zip_code}
                        </Typography>
                    </Box>
                </Box>
            </Grid>
        </Grid>
    </ProfileHeader>
);

// Mode toggle using context values
const ModeToggle = ({ mode, toggleMode }) => (
    <ModeToggleContainer>
        <ToggleButton active={mode === 'Borrowing'} onClick={() => toggleMode('Borrowing')}>
            Borrower
        </ToggleButton>
        <ToggleButton active={mode === 'Lending'} onClick={() => toggleMode('Lending')}>
            Lender
        </ToggleButton>
    </ModeToggleContainer>
);

// Personal information section (without the pencil icon)
const PersonalInformation = ({ isEditing, user, editableUser, newPassword, setEditableUser, setNewPassword }) => (
    <StyledSection>
        <Typography variant="h6" gutterBottom>
            Personal Information
        </Typography>
        {isEditing ? (
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <StyledTextField
                        fullWidth
                        label="First Name"
                        value={editableUser.first_name}
                        onChange={(e) => setEditableUser({ ...editableUser, first_name: e.target.value })}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <StyledTextField
                        fullWidth
                        label="Last Name"
                        value={editableUser.last_name}
                        onChange={(e) => setEditableUser({ ...editableUser, last_name: e.target.value })}
                    />
                </Grid>
                <Grid item xs={12}>
                    <StyledTextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={editableUser.email}
                        onChange={(e) => setEditableUser({ ...editableUser, email: e.target.value })}
                    />
                </Grid>
                <Grid item xs={12}>
                    <StyledTextField
                        fullWidth
                        label="Zip Code"
                        value={editableUser.zip_code}
                        onChange={(e) => setEditableUser({ ...editableUser, zip_code: e.target.value })}
                    />
                </Grid>
                <Grid item xs={12}>
                    <StyledTextField
                        fullWidth
                        label="New Password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        helperText="Leave blank to keep current password"
                    />
                </Grid>
            </Grid>
        ) : (
            <List>
                <ListItem>
                    <ListItemIcon>
                        <Email color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Email" secondary={user.email} />
                </ListItem>
                <ListItem>
                    <ListItemIcon>
                        <LocationIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Location" secondary={user.zip_code} />
                </ListItem>
            </List>
        )}
    </StyledSection>
);
// ----- Onboarding Progress -----
const OnboardingProgress = ({
    user,
    stripeRequirements,
    handleConnectStripe,
    handleVerification,
    verificationLoading,
    requirementDescriptions,
    showStripeDetails,
    setShowStripeDetails,
    connectingStripe,
    stripeLoadingMessage
}) => {
    const paymentDone =
        user.stripe_connected_account_id &&
        stripeRequirements.currently_due.length === 0;
    const paymentPending =
        user.stripe_connected_account_id &&
        stripeRequirements.currently_due.length > 0;

    return (
        <StyledSection>
            <Typography variant="h6" gutterBottom>
                Onboarding Progress
            </Typography>

            {/* 1) Email */}
            <Box display="flex" alignItems="center" gap={2} mb={1}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: user.is_active ? '#4CAF50' : '#E0E0E0',
                        color: user.is_active ? '#FFF' : '#616161',
                    }}
                >
                    {user.is_active ? <CheckCircleIcon /> : '1'}
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1">Verify Email</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {user.is_active
                            ? 'Your email is verified'
                            : 'Check your inbox for a verification link'}
                    </Typography>
                </Box>
                {!user.is_active && (
                    <Button variant="outlined" onClick={() => {/* resend logic */ }}>
                        Resend Email
                    </Button>
                )}
            </Box>

            {/* 2) Identity */}
            <Box display="flex" alignItems="center" gap={2} mb={1}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor:
                            user.persona_status === 'approved' || user.persona_status === "completed" ? '#4CAF50' : '#E0E0E0',
                        color: user.persona_status === 'approved' || user.persona_status === "completed" ? '#FFF' : '#616161',
                    }}
                >
                    {user.persona_status === 'approved' || user.persona_status === "completed" ? <CheckCircleIcon /> : '2'}
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1">Verify Identity</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {user.persona_status === 'approved'
                            ? 'Your identity is verified'
                            : 'Required to rent items'}
                    </Typography>
                </Box>
                {(user.persona_status !== 'approved' &&
                    user.persona_status !== 'completed') && (
                        <Button
                            variant="outlined"
                            onClick={handleVerification}
                            disabled={verificationLoading}
                        >
                            Verify Now
                        </Button>
                    )}
            </Box>

            {/* 3) Payment */}
            <Box display="flex" alignItems="center" gap={2}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: paymentDone ? '#4CAF50' : '#E0E0E0',
                        color: paymentDone ? '#FFF' : '#616161',
                    }}
                >
                    {paymentDone ? <CheckCircleIcon /> : '3'}
                </Box>
                <Tooltip
                    title={
                        paymentPending
                            ? 'Almost there! Finish setting up your payment account so you can start getting paid.'
                            : ''
                    }
                    placement="right"
                    arrow
                >
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            p: paymentPending ? 1 : 0,
                            backgroundColor: paymentPending ? '#FFF8E1' : 'transparent',
                            borderRadius: 1,
                            transition: 'background-color 0.3s',
                        }}
                    >
                        <Typography variant="subtitle1">Connect Payment</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {paymentDone
                                ? 'Your payment account is ready'
                                : user.stripe_connected_account_id
                                    ? 'Almost done! Complete a few steps to start receiving payouts.'
                                    : 'Required to lend items'}
                        </Typography>

                        {paymentPending && (
                            <>
                                <Button
                                    size="small"
                                    onClick={() => setShowStripeDetails((p) => !p)}
                                    endIcon={showStripeDetails ? <ExpandLess /> : <ExpandMore />}
                                    sx={{ mt: 1, alignSelf: 'flex-start' }}
                                >
                                    {showStripeDetails ? 'Hide Details' : 'See What’s Missing'}
                                </Button>

                                <Collapse in={showStripeDetails}>
                                    <List dense sx={{ mt: 1 }}>
                                        {stripeRequirements.currently_due.map((req, i) => (
                                            <ListItem key={i}>
                                                <ListItemIcon>
                                                    <WarningIcon fontSize="small" color="warning" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        requirementDescriptions[req] ||
                                                        req.replace(/\./g, ' ').toUpperCase()
                                                    }
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapse>
                            </>
                        )}
                    </Box>
                </Tooltip>

                {(!user.stripe_connected_account_id || stripeRequirements.currently_due.length > 0) && (
                    <Box>
                        <LoadingButton
                            variant="outlined"
                            onClick={handleConnectStripe}
                            loading={connectingStripe}
                            loadingIndicator={
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <CircularProgress size={16} />
                                    <Typography variant="caption" sx={{ mt: 0.5 }}>
                                        {stripeLoadingMessage}
                                    </Typography>
                                </Box>
                            }
                        >
                            {user.stripe_connected_account_id ? 'Complete Setup' : 'Connect Stripe'}
                        </LoadingButton>
                    </Box>
                )}

            </Box>

            {/* Footer hint */}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {(user.is_active &&
                    (user.persona_status === 'approved' || user.persona_status === 'completed') &&
                    paymentDone)
                    ? "You're all set! Start lending and renting items."
                    : 'Complete all steps to unlock full platform access'}
            </Typography>
        </StyledSection>
    );
};

// Transaction history (only for borrowers)
const TransactionHistorySection = ({
    user,
    transactions,
    invoiceLoaded,
    loadingTransactions,
    fetchTransactions,
    connectingStripe,
    handleConnectStripe,
    stripeLoadingMessage
}) => {
    if (user.role !== 'Borrowing') return null;
    return (
        <StyledSection>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Transaction History</Typography>
                {invoiceLoaded && (
                    <LoadingButton onClick={fetchTransactions} loading={loadingTransactions} startIcon={<RefreshIcon />} variant="outlined">
                        Refresh
                    </LoadingButton>
                )}
            </Box>
            {!user.stripe_connected_account_id ? (
                <Box textAlign="center" py={3}>
                    <Typography color="text.secondary">Connect your Stripe account to view transactions</Typography>
                    <Button
                        variant="contained"
                        onClick={handleConnectStripe}
                        sx={{ mt: 2 }}
                        disabled={connectingStripe}
                    >
                        {connectingStripe ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                {stripeLoadingMessage}
                            </Box>
                        ) : (
                            'Connect Stripe'
                        )}
                    </Button>

                </Box>
            ) : !invoiceLoaded ? (
                <Box textAlign="center" py={3}>
                    <Typography color="text.secondary">Click below to load your invoice.</Typography>
                    <Button variant="contained" onClick={fetchTransactions} disabled={loadingTransactions} sx={{ mt: 2 }}>
                        {loadingTransactions ? <CircularProgress size={24} /> : 'Load Invoice'}
                    </Button>
                </Box>
            ) : loadingTransactions ? (
                <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress />
                </Box>
            ) : (
                <TransactionTable transactions={transactions} />
            )}
        </StyledSection>
    );
};

// Account settings section
const AccountSettingsSection = ({ navigate }) => (
    <StyledSection>
        <Typography variant="h6" gutterBottom>Account Settings</Typography>
        <List>
            <SettingsItem button onClick={() => navigate('/payment-details')}>
                <ListItemIcon>
                    <PaymentIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Payment Details" secondary="Manage payment methods and transactions" />
            </SettingsItem>
            <SettingsItem button onClick={() => navigate('/how-it-works')}>
                <ListItemIcon>
                    <HelpOutlineIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Help & Support" secondary="FAQs and contact information" />
            </SettingsItem>
            <SettingsItem button onClick={() => navigate('/about')}>
                <ListItemIcon>
                    <InfoIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="About Borrowed" secondary="Learn about our mission and team" />
            </SettingsItem>
            {/* <SettingsItem button onClick={() => navigate('/blocked-users')}>
                <ListItemIcon>
                    <BlockIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Blocked Users" secondary="Manage your blocked users list" />
            </SettingsItem> */}
        </List>
    </StyledSection>
);

// Edit action buttons placed above the logout section
const EditActions = ({ isEditing, handleSave, handleCancel, setIsEditing }) => (
    <Box display="flex" flexDirection="column" gap={2} mb={2}>
        {isEditing ? (
            <>
                <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={handleSave}>
                    Save Changes
                </Button>
                <Button variant="outlined" color="secondary" startIcon={<CancelIcon />} onClick={handleCancel}>
                    Cancel
                </Button>
            </>
        ) : (
            <Button variant="outlined" color="primary" startIcon={<EditIcon />} onClick={() => setIsEditing(true)}>
                Edit Profile
            </Button>
        )}
    </Box>
);

// Logout & Delete Account buttons (logout is now red)
const LogoutDeleteButtons = ({ handleLogout, initiateDeleteAccount }) => (
    <Box display="flex" flexDirection="column" gap={2} mb={2}>
        <Button variant="outlined" color="error" onClick={handleLogout}>
            Logout
        </Button>
        <Button variant="outlined" color="error" onClick={initiateDeleteAccount}>
            Delete Account
        </Button>
    </Box>
);

// Location update dialog
const LocationDialog = ({ open, onClose, editableUser, handleLocationChange }) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Update Location</DialogTitle>
        <DialogContent>
            <MapWrapper>
                <MapContainer
                    center={[editableUser.latitude || 40.7128, editableUser.longitude || -74.0060]}
                    zoom={13}
                    style={{ height: '100%', width: '100%' }}
                >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    {editableUser.latitude && editableUser.longitude && (
                        <Circle center={[editableUser.latitude, editableUser.longitude]} radius={1000} pathOptions={{ color: '#4CAF50' }} />
                    )}
                    <LocationPicker onLocationChange={handleLocationChange} />
                </MapContainer>
            </MapWrapper>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Close</Button>
        </DialogActions>
    </Dialog>
);

// Delete account confirmation dialog
const DeleteAccountDialog = ({
    open,
    onClose,
    deleteDialogStep,
    deleteConfirmationText,
    setDeleteConfirmationText,
    handleDeleteDialogNext,
    handleFinalDelete,
}) => (
    <Dialog open={open} onClose={onClose}>
        <DialogTitle>
            {deleteDialogStep === 1 && "Delete Account"}
            {deleteDialogStep === 2 && "Warning"}
            {deleteDialogStep === 3 && "Final Verification"}
        </DialogTitle>
        <DialogContent>
            {deleteDialogStep === 1 && (
                <Typography>
                    Are you sure you want to delete your account? This action cannot be undone.
                </Typography>
            )}
            {deleteDialogStep === 2 && (
                <Typography>
                    Deleting your account will remove all your data, listings, and transaction history. This cannot be recovered.
                </Typography>
            )}
            {deleteDialogStep === 3 && (
                <>
                    <Typography sx={{ mb: 1 }}>
                        To confirm account deletion, please type <strong>DELETE</strong> in the box below.
                    </Typography>
                    <TextField
                        fullWidth
                        value={deleteConfirmationText}
                        onChange={(e) => setDeleteConfirmationText(e.target.value)}
                        placeholder="Type DELETE to confirm"
                    />
                </>
            )}
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            {deleteDialogStep < 3 ? (
                <Button onClick={handleDeleteDialogNext} color="error">
                    Continue
                </Button>
            ) : (
                <Button onClick={handleFinalDelete} color="error" disabled={deleteConfirmationText !== 'DELETE'}>
                    Delete
                </Button>
            )}
        </DialogActions>
    </Dialog>
);

// Map Location Picker (used in LocationDialog)
function LocationPicker({ onLocationChange }) {
    useMapEvents({
        click(e) {
            onLocationChange(e.latlng);
        },
    });
    return null;
}

// ----- Main Profile Component -----
function Profile() {
    const navigate = useNavigate();
    const { logout: authLogout } = useAuth();
    const { mode, toggleMode } = useMode();
    const [user, setUser] = useState(null);
    const [editableUser, setEditableUser] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [stripeRequirements, setStripeRequirements] = useState({ currently_due: [], eventually_due: [], past_due: [] });
    const [connectingStripe, setConnectingStripe] = useState(false);
    const [locationDialogOpen, setLocationDialogOpen] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [verificationLoading, setVerificationLoading] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [loadingTransactions, setLoadingTransactions] = useState(false);
    const [invoiceLoaded, setInvoiceLoaded] = useState(false);

    // Delete account flow states
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteDialogStep, setDeleteDialogStep] = useState(1);
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
    const [stripeLoadingMessage, setStripeLoadingMessage] = useState("");



    const [showStripeDetails, setShowStripeDetails] = useState(false);

    const requirementDescriptions = {
        'business_profile.mcc': 'Select a business category (MCC)',
        'business_profile.url': 'Add your business website or online presence',
        'external_account': 'Link a bank account to receive payouts',
        'individual.address.city': 'Enter your city',
        'individual.address.line1': 'Enter your street address',
        'individual.address.postal_code': 'Enter your postal/ZIP code',
        'individual.address.state': 'Enter your state or region',
        'individual.dob.day': 'Enter your date of birth (day)',
        'individual.dob.month': 'Enter your date of birth (month)',
        'individual.dob.year': 'Enter your date of birth (year)',
        'individual.email': 'Enter your email address',
        'individual.first_name': 'Enter your first name',
        'individual.last_name': 'Enter your last name',
        'individual.phone': 'Enter your phone number',
        'individual.ssn_last_4': 'Provide the last 4 digits of your SSN',
        'settings.payments.statement_descriptor': 'Customize the statement name shown on customer bank statements',
        'tos_acceptance.date': 'Accept Stripe’s Terms of Service',
        'tos_acceptance.ip': 'Accept Stripe’s Terms of Service from a valid IP',
    };

    const fetchTransactions = async () => {
        setLoadingTransactions(true);
        try {
            const response = await getStripeTransactions();
            setTransactions(response.data);
            setInvoiceLoaded(true);
        } catch (error) {
            console.error('Error fetching transactions:', error);
            setSnackbar({ open: true, message: 'Failed to load transactions', severity: 'error' });
        } finally {
            setLoadingTransactions(false);
        }
    };

    const fetchUserProfile = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getCurrentUser();
            setUser(response.data);
            setEditableUser(response.data);
            if (response.data.stripe_connected_account_id) {
                await fetchStripeStatus();
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
            setSnackbar({ open: true, message: 'Failed to load profile data', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUserProfile();
    }, [fetchUserProfile]);

    const handleProfilePictureChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            try {
                await uploadProfilePicture(file);
                await fetchUserProfile();
                setSnackbar({ open: true, message: 'Profile picture updated successfully', severity: 'success' });
            } catch (error) {
                console.error('Error uploading profile picture:', error);
                setSnackbar({ open: true, message: 'Failed to update profile picture', severity: 'error' });
            }
        }
    };

    const handleDeleteProfilePicture = async () => {
        try {
            await deleteProfilePicture();
            await fetchUserProfile();
            setSnackbar({ open: true, message: 'Profile picture deleted successfully', severity: 'success' });
        } catch (error) {
            console.error('Error deleting profile picture:', error);
            setSnackbar({ open: true, message: 'Failed to delete profile picture', severity: 'error' });
        }
    };

    const fetchStripeStatus = async () => {
        try {
            const statusResponse = await getAccountStatus();
            const data = statusResponse.data || {};
            setStripeRequirements({
                currently_due: parsePythonListString(data.requirements_currently_due),
                eventually_due: parsePythonListString(data.requirements_eventually_due),
                past_due: parsePythonListString(data.requirements_past_due),
            });
        } catch (error) {
            console.error('Error fetching Stripe status:', error);
            setSnackbar({ open: true, message: 'Failed to load Stripe status', severity: 'error' });
        }
    };
    const handleLocationChange = (newLocation) => {
        setEditableUser((prev) => ({
            ...prev,
            latitude: newLocation.lat,
            longitude: newLocation.lng,
        }));
        setLocationDialogOpen(false);
    };

    const handleSave = async () => {
        try {
            const dataToUpdate = { ...editableUser };
            if (newPassword) {
                dataToUpdate.password = newPassword;
            }
            await updateUser(dataToUpdate);
            setUser(dataToUpdate);
            setIsEditing(false);
            setSnackbar({
                open: true,
                message: 'Profile updated successfully',
                severity: 'success',
            });
        } catch (error) {
            console.error('Error updating profile:', error);
            setSnackbar({
                open: true,
                message: 'Failed to update profile',
                severity: 'error',
            });
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
            authLogout();
            navigate('/');
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    const handleConnectStripe = async () => {
        if (connectingStripe) return;
        setConnectingStripe(true);
        try {
            if (!user.stripe_connected_account_id) {
                setStripeLoadingMessage("Creating Stripe Account...");
                const accountResponse = await createConnectedAccount();
                console.log('Connected Account:', accountResponse.data);
            }
            setStripeLoadingMessage("Linking Stripe Account...");
            const linkResponse = await createAccountLink();

            // show final message + dots animation
            setStripeLoadingMessage("One second please");
            let dots = "";
            const dotsInterval = setInterval(() => {
                dots = dots.length >= 3 ? "" : dots + ".";
                setStripeLoadingMessage(`One second please${dots}`);
            }, 500);

            // delay redirect so animation shows
            setTimeout(() => {
                clearInterval(dotsInterval);
                window.location.href = linkResponse.data.url;
            }, 2000);
        } catch (error) {
            console.error('Error connecting Stripe:', error);
            setStripeLoadingMessage("");
            setSnackbar({
                open: true,
                message: 'Failed to connect Stripe account',
                severity: 'error',
            });
        } finally {
            // only reset if we caught an error (on success we’re redirecting)
            setConnectingStripe(prev => {
                // if still loading (i.e. no redirect), turn it off
                return window.location.href ? prev : false;
            });
        }
    };

    const handleVerification = () => {
        setVerificationLoading(true);
        const client = new Persona.Client({
            templateId: process.env.REACT_APP_PERSONA_TEMPLATE_ID,
            // environment: 'sandbox',
            environmentId: process.env.REACT_APP_PERSONA_ENVIRONMENT_ID,
            referenceId: user.id.toString(),
            onComplete: async ({ inquiryId, status }) => {
                try {
                    await submitPersonaVerification({ inquiryId, status, userId: user.id });
                    await fetchUserProfile();
                    setSnackbar({
                        open: true,
                        message: 'Verification completed successfully',
                        severity: 'success',
                    });
                } catch (error) {
                    setSnackbar({
                        open: true,
                        message: 'Verification failed',
                        severity: 'error',
                    });
                } finally {
                    setVerificationLoading(false);
                }
            },
            onError: (error) => {
                console.error('Verification error:', error);
                setVerificationLoading(false);
                setSnackbar({
                    open: true,
                    message: 'Verification error occurred',
                    severity: 'error',
                });
            },
        });
        client.open();
    };

    // Delete account flow handlers
    const initiateDeleteAccount = () => {
        setDeleteDialogStep(1);
        setDeleteConfirmationText('');
        setDeleteDialogOpen(true);
    };

    const handleDeleteDialogNext = () => {
        if (deleteDialogStep === 1) {
            setDeleteDialogStep(2);
        } else if (deleteDialogStep === 2) {
            setDeleteDialogStep(3);
        }
    };

    const handleFinalDelete = async () => {
        if (deleteConfirmationText === 'DELETE') {
            try {
                await deleteUserAccount();
                handleLogout();
                setSnackbar({
                    open: true,
                    message: 'Account deleted successfully',
                    severity: 'success',
                });
            } catch (error) {
                console.error('Error deleting account:', error);
                setSnackbar({
                    open: true,
                    message: 'There was a problem deleting your account',
                    severity: 'error',
                });
            } finally {
                setDeleteDialogOpen(false);
            }
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!user) return null;

    return (
        <StyledContainer>
            <ProfileCard
                user={user}
                isEditing={isEditing}
                handleProfilePictureChange={handleProfilePictureChange}
                handleDeleteProfilePicture={handleDeleteProfilePicture}
            />
            <ModeToggle mode={mode} toggleMode={toggleMode} />
            <QuickActions mode={mode} navigate={navigate} />
            <PersonalInformation
                isEditing={isEditing}
                user={user}
                editableUser={editableUser}
                newPassword={newPassword}
                setEditableUser={setEditableUser}
                setNewPassword={setNewPassword}
            />
            <OnboardingProgress
                user={user}
                stripeRequirements={stripeRequirements}
                handleConnectStripe={handleConnectStripe}
                handleVerification={handleVerification}
                verificationLoading={verificationLoading}
                requirementDescriptions={requirementDescriptions}
                showStripeDetails={showStripeDetails}
                setShowStripeDetails={setShowStripeDetails}
                connectingStripe={connectingStripe}
                stripeLoadingMessage={stripeLoadingMessage}
            />
            <TransactionHistorySection
                user={user}
                transactions={transactions}
                invoiceLoaded={invoiceLoaded}
                loadingTransactions={loadingTransactions}
                fetchTransactions={fetchTransactions}
                connectingStripe={connectingStripe}
                handleConnectStripe={handleConnectStripe}
                stripeLoadingMessage={stripeLoadingMessage}
            />
            {/* <ActiveListingsSection user={user} navigate={navigate} /> */}
            <AccountSettingsSection navigate={navigate} />
            <EditActions
                isEditing={isEditing}
                handleSave={handleSave}
                handleCancel={() => {
                    setIsEditing(false);
                    setEditableUser(user);
                    setNewPassword('');
                }}
                setIsEditing={setIsEditing}
            />
            <LogoutDeleteButtons
                handleLogout={handleLogout}
                initiateDeleteAccount={initiateDeleteAccount}
            />
            <LocationDialog
                open={locationDialogOpen}
                onClose={() => setLocationDialogOpen(false)}
                editableUser={editableUser}
                handleLocationChange={handleLocationChange}
            />
            <DeleteAccountDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                deleteDialogStep={deleteDialogStep}
                deleteConfirmationText={deleteConfirmationText}
                setDeleteConfirmationText={setDeleteConfirmationText}
                handleDeleteDialogNext={handleDeleteDialogNext}
                handleFinalDelete={handleFinalDelete}
            />
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </StyledContainer>
    );
}

export default Profile;
